import { isValidPhoneNumber } from 'libphonenumber-js';
import _ from 'lodash';
import { auth } from '../auth';

/**
 * Validates a phone number for a given country. Rejects an empty phone, strips hyphens, and defers to libphonenumber's isValidPhoneNumber with the country code, which defaults to US.
 *
 * @param phone - Phone number, possibly hyphen-separated.
 * @param countryCode - ISO country code used for validation, defaulting to US.
 * @returns True when the digits form a valid phone number for the country.
 */
const isValidPhone = (phone = '', countryCode = 'US') => {
  if (!phone) {
    return false;
  }

  const phoneNumber = phone.replace(/-/g, '');
  return isValidPhoneNumber(phoneNumber, countryCode as any);
};

/**
 * Reports whether an element with the given id currently exists in the document. Returns true when document.getElementById finds a non-null node and false otherwise.
 *
 * @param idString - Element id to look up in the DOM.
 * @returns True when the element exists in the document.
 */
const checkNullId = (idString: any) => {
  if (document.getElementById(idString) !== null) {
    return true;
  }

  return false;
};

/**
 * Reports whether a value is a non-negative integer id. Stringifies the input and tests it against a regex allowing an optional plus sign followed by zero or a digit sequence without leading zeros.
 *
 * @param s - Candidate id value.
 * @returns True when the value reads as a non-negative integer.
 */
const isIdNumber = (s: any) => {
  const regex = /^\+?(0|[0-9]\d*)$/;
  return regex.test(String(s));
};

/**
 * Locates the option in a list whose value field equals the given value. Delegates to Array.find comparing each element's value property.
 *
 * @param array - List of option objects to search.
 * @param value - Value to match against each element's value property.
 * @returns The matching element, or undefined when none matches.
 */
const findArrayElementByValue = (array: any, value: any) =>
  array.find((element: any) => element.value === value);

/**
 * Reports whether a value consists only of digits. Tests the value against a regex requiring one or more digit characters end to end.
 *
 * @param value - Value to test.
 * @returns True when the value contains only digits.
 */
const isNumberByRegex = (value: any) => /^\d+$/.test(value);

/**
 * Decides whether the comment icon should be shown for an item authored by another user. Returns true only when an id is present and differs from the currently authenticated user id.
 *
 * @param id - Author id attached to the item.
 * @returns True when the id belongs to someone other than the current user.
 */
const checkShowCommentIcon = (id: any) => {
  if (id && id !== auth.userId()) {
    return true;
  }

  return false;
};

/**
 * Determines whether a form value counts as empty, treating all-falsy objects and arrays as empty. Applies the single-value check that flags falsy values and objects whose keys are all falsy, then repeats that check across every array element.
 *
 * @param value - Value, object, or array to test for emptiness.
 * @returns True when the value, or every element of it, is falsy or all-falsy-keyed.
 */
const isFalsyOrEmpty = (value: any) => {
  // Function to check if a single value is falsy or an object with all falsy keys
  /**
   * Checks whether a single value is falsy or an object whose every key holds a falsy value. Returns true for falsy input and for plain non-array objects where every key maps to a falsy value, false otherwise.
   *
   * @param val - Value to test.
   * @returns True when the value is falsy or an all-falsy-keyed object.
   */
  const isSingleValueFalsyOrObjectWithFalsyKeys = (val: any) => {
    if (!val) {
      return true;
    }

    if (_.isObject(val) && !_.isArray(val) && _.every(_.keys(val), (key) => !(val as any)[key])) {
      return true;
    }

    return false;
  };

  // Check for falsy values or objects with all falsy keys
  if (isSingleValueFalsyOrObjectWithFalsyKeys(value)) {
    return true;
  }

  // Check for array with all elements as falsy or objects with all falsy keys
  if (_.isArray(value) && _.every(value, (el) => isSingleValueFalsyOrObjectWithFalsyKeys(el))) {
    return true;
  }

  // All other cases
  return false;
};

export {
  checkNullId,
  checkShowCommentIcon,
  findArrayElementByValue,
  isFalsyOrEmpty,
  isIdNumber,
  isNumberByRegex,
  isValidPhone,
};
